import React, { useState, useCallback } from 'react';
import { AdvancedMarker, InfoWindow, MapControl, ControlPosition } from '@vis.gl/react-google-maps';
import { useInstantSearch, Configure } from 'react-instantsearch';
import { useDrawingManager } from './EstateDrawingManager';
import { useTranslation } from 'react-i18next';

const SearchMapContent = ({ hits }) => {
    const { setIndexUiState } = useInstantSearch();
    const [selectedEstate, setSelectedEstate] = useState(null);
    const [isInfoWindowOpen, setIsInfoWindowOpen] = useState(false);
    const [isDrawingMode, setIsDrawingMode] = useState(false);
    const [polygonPoints, setPolygonPoints] = useState(null);

    const handlePolygonComplete = useCallback((polygon) => {
        const path = polygon.getPath();

        // Convert polygon points to Algolia format: [[lat, lng], [lat, lng], ...]
        const points = path.getArray().map(point => [point.lat(), point.lng()]);

        // Make sure to close the polygon by adding the first point at the end if needed
        if (points.length > 0 &&
            (points[0][0] !== points[points.length - 1][0] ||
                points[0][1] !== points[points.length - 1][1])) {
            points.push(points[0]);
        }

        setPolygonPoints(points);

        // Update Algolia state with insidePolygon parameter
        setIndexUiState(state => ({
            ...state,
            insidePolygon: [points.flat()]
        }));

        setIsDrawingMode(false);
    }, [setIndexUiState]);

    const { startDrawing, clearOverlay, activeOverlay, isReady } = useDrawingManager(handlePolygonComplete);

    const { t } = useTranslation('common');

    const BlueMarker = () => (
        <div className="size-6 bg-sky-600 rounded-full border-2 border-white shadow-lg hover:bg-sky-600 transition-colors" />
    );

    const handleMarkerClick = useCallback((estate) => {
        setSelectedEstate(estate);
        setIsInfoWindowOpen(true);
        const element = document.getElementById(`estate-${estate.objectID}`);
        if (element) {
            element.scrollIntoView({ behavior: 'smooth' });
        }
    }, []);

    const handleInfoWindowClose = useCallback(() => {
        setIsInfoWindowOpen(false);
    }, []);

    const scrollToResults = useCallback(() => {
        // First try to find the InfiniteHits component
        const resultsElement = document.querySelector('.ais-InfiniteHits');

        if (resultsElement) {
            // Add offset for header height and any other fixed elements
            const headerOffset = 100; // Adjust this value based on your layout
            const elementPosition = resultsElement.getBoundingClientRect().top;
            const offsetPosition = elementPosition + window.pageYOffset - headerOffset;

            window.scrollTo({
                top: offsetPosition,
                behavior: 'smooth'  // Add smooth scrolling
            });
        }
    }, []);

    const handleStartDrawing = useCallback(() => {
        if (isReady) {
            startDrawing();
            setIsDrawingMode(true);
        }
    }, [isReady, startDrawing]);

    const clearPolygon = useCallback(() => {
        clearOverlay();
        setPolygonPoints(null);
        setIndexUiState(state => ({
            ...state,
            insidePolygon: undefined
        }));
    }, [clearOverlay, setIndexUiState]);

    const [hoveredEstate, setHoveredEstate] = useState(null);

    // Modify handleMarkerClick to handle view button click instead of direct click
    const handleViewEstate = useCallback((estate) => {
        window.location.href = `/estate/${estate.id}`;
    }, []);

    // Add marker hover handlers
    const handleMarkerMouseOver = useCallback((estate) => {
        setHoveredEstate(estate);
    }, []);

    const handleMarkerMouseOut = useCallback(() => {
        setHoveredEstate(null);
    }, []);

    return (
        <>
            <Configure
                insidePolygon={polygonPoints ? [polygonPoints.flat()] : undefined}
                hitsPerPage={1000}
            />

            {hits.map(estate => {
                if (estate._geoloc) {
                    return (
                        <AdvancedMarker
                            key={estate.objectID}
                            position={{
                                lat: estate._geoloc.lat,
                                lng: estate._geoloc.lng
                            }}
                            onClick={() => setSelectedEstate(estate)}
                            onMouseOver={() => handleMarkerMouseOver(estate)}
                            onMouseOut={handleMarkerMouseOut}
                        >
                            <BlueMarker />
                        </AdvancedMarker>
                    );
                }
                return null;
            })}

            {(selectedEstate || hoveredEstate) && (
                <InfoWindow
                    position={{
                        lat: (selectedEstate || hoveredEstate)._geoloc.lat,
                        lng: (selectedEstate || hoveredEstate)._geoloc.lng
                    }}
                    onCloseClick={() => {
                        setSelectedEstate(null);
                        setIsInfoWindowOpen(false);
                    }}
                >
                    <div className="max-w-xs">
                        <img className="w-full h-auto" src={(selectedEstate || hoveredEstate).thumb}></img>
                        <h3 className="font-bold text-lg mb-1">{(selectedEstate || hoveredEstate).title}</h3>
                        <p className="text-green-600 font-bold mb-2">
                            {new Intl.NumberFormat('da-DK', {
                                style: 'currency',
                                currency: 'EUR'
                            }).format((selectedEstate || hoveredEstate).price)}
                        </p>
                        <div className="flex justify-between text-sm mb-3">
                            <span>{(selectedEstate || hoveredEstate).bedrooms} {t('map.rooms')}</span>
                            <span>{(selectedEstate || hoveredEstate).bathrooms} {t('map.bathrooms')}</span>
                            <span>{(selectedEstate || hoveredEstate).living_area} {t('map.squareMeters')}</span>
                        </div>
                        <button
                            onClick={() => handleViewEstate(selectedEstate || hoveredEstate)}
                            className="w-full bg-sky-700 text-white text-sm font-semibold py-2 px-4 rounded hover:bg-sky-600 transition-colors"
                        >
                            {t('map.viewProperty')}
                        </button>
                    </div>
                </InfoWindow>
            )}

            <MapControl position={ControlPosition.BOTTOM_CENTER}>
                <div className="mb-8">
                    {isDrawingMode && !activeOverlay && (
                        <div className="bg-white/90 text-gray-800 font-semibold text-xs tracking-wider uppercase py-2.5 px-4 rounded-lg shadow-lg">
                            {t('map.drawHint', 'Click and drag on the map to draw an area')}
                        </div>
                    )}

                    {!isDrawingMode && !activeOverlay && (
                        <button
                            onClick={handleStartDrawing}
                            className="bg-sky-700 uppercase font-semibold text-xs tracking-wider text-white py-2.5 px-4 rounded-lg hover:bg-sky-900 hover:text-white transition-colors shadow-lg"
                        >
                            {t('map.drawArea')}
                        </button>
                    )}

                    {activeOverlay && (
                        <div className="flex gap-2">
                            <button
                                onClick={clearPolygon}
                                className="border border-red-500 text-red-600 font-semibold text-xs tracking-wider uppercase bg-white py-2 px-4 rounded-full hover:bg-red-700 hover:text-white transition-colors shadow-lg"
                            >
                                {t('map.clearArea')}
                            </button>
                            <button
                                onClick={scrollToResults}
                                className="bg-white font-semibold text-xs tracking-wider uppercase py-2 px-4 rounded-full hover:bg-green-700 transition-colors shadow-lg"
                            >
                                {t('map.viewProperties', { count: hits.length })} <span aria-hidden="true">→</span>
                            </button>
                        </div>
                    )}
                </div>
            </MapControl>

            <MapControl position={ControlPosition.TOP_CENTER}>

            </MapControl>
        </>
    );
};

export default SearchMapContent;